import { useEffect, useMemo, useRef, useState } from 'react';
import type { PlayerFinderTimeOfDayEnum } from 'types/generated/client';
import { venueDistanceMiles } from 'utils/shared/geo/venueDistanceMiles';
import {
  AVAILABILITY_DAY_OPTIONS,
  type AvailabilityDay,
  TIME_OF_DAY_OPTIONS,
  dayOfWeekFromDate,
} from 'utils/shared/playerAvailability';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useModal } from 'hooks/useModal';
import ArrowRight from 'svg/ArrowRight';
import Calendar from 'svg/Calendar';
import CloseIcon from 'svg/CloseIcon';
import Erase from 'svg/Erase';
import InfoCircle from 'svg/InfoCircle';
import PlusPlain from 'svg/PlusPlain';
import {
  COURT_TYPE_OPTIONS,
  DAY_OPTIONS,
  DISTANCE_OPTIONS,
  type FindFilterVariant,
  type FindFilters,
  type FindSortKey,
  GAME_SORT_OPTIONS,
  GAME_TYPE_OPTIONS,
  GENDER_OPTIONS,
  HOME_DEFAULT_DISTANCE_BONUS_MILES,
  HOME_DISTANCE_OPTIONS,
  PLAYER_SORT_OPTIONS,
  PRICE_MAX,
  PRICE_MIN,
  SKILL_OPTIONS,
  TIME_OPTIONS,
  TYPE_OPTIONS,
  makeDefaultFilters,
} from 'screens/Find/filters';
import { DEFAULT_SEARCH_CENTER_NAME } from 'screens/Find/geo';
import type { VenueAddressWithCity } from 'screens/ListGame/types';
import { toVenueAddress } from 'screens/ListGame/utils';
import { Button, ButtonText } from 'components/Button';
import CardsSection from 'components/CardSection';
import SkillLevelInfoModal from 'components/Explore/SkillLevelInfoModal';
import FilterChipGroup, { type FilterChipOption } from 'components/Find/FilterChipGroup';
import LocationCard from 'components/Find/LocationCard';
import {
  type Neighborhood,
  NeighborhoodSelect,
  useNeighborhoods,
} from 'components/Find/Neighborhoods';
import InfoAlert from 'components/InfoAlert';
import Pill from 'components/Pill';
import AddVenue from 'components/forms/AddVenue';
import type { AddVenueResult } from 'components/forms/AddVenue/props';
import SliderNumberRange from 'components/forms/Slider/SliderNumberRange';
import Switch from 'components/forms/Switch';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';
import FilterSection from './FilterSection';

/** Array-valued filter keys — the multi-select sections toggle/clear these. */
type ArrayFilterKey =
  | 'daysOfWeek'
  | 'timesOfDay'
  | 'formats'
  | 'competitiveness'
  | 'genders'
  | 'skillLevels'
  | 'courtTypes';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  variant: FindFilterVariant;
  /** The committed filters — staged edits start from these each time it opens. */
  filters: FindFilters;
  onApply: (filters: FindFilters) => void;
  /** Counts results for a given filter set — drives the live "Show N" footer.
   *  Memoised by the caller over the current list. Omit for a plain "Apply". */
  getResultCount?: (filters: FindFilters) => number;
  /**
   * Home only: the bar's date pill is "Today", so day chips are inert (greyed,
   * selection kept) and only today's time-of-day row shows.
   */
  isTodayMode?: boolean;
  /** Home only: shows the "Pre-selected based on your preferences" notice. */
  preferencesNotice?: { onViewPreferences: () => void };
  /** Home only: candidate venues for "Preferred courts" (the player's courts + any added). */
  venueOptions?: VenueAddressWithCity[];
  /**
   * Home only: "Learn more" / "Add a venue" swap this sheet for another modal (one
   * modal at a time); this reopens the sheet when that modal closes, keeping the
   * staged edits.
   */
  onReopen?: () => void;
}

/**
 * The "Filters" sheet (bottom-sheet on mobile, card on desktop). Games and Players
 * (/find) share the shell + chip atoms; `home` renders the player-home section
 * set (youth toggle, availability pattern, venues, radius bonus, price). Edits are
 * staged locally and committed on Show N / Apply; "Clear all" resets.
 */
export default function FiltersModal({
  isOpen,
  onClose,
  variant,
  filters,
  onApply,
  getResultCount,
  isTodayMode = false,
  preferencesNotice,
  venueOptions,
  onReopen,
}: Props) {
  const isGames = variant === 'games';
  const isHome = variant === 'home';
  const [staged, setStaged] = useState<FindFilters>(filters);
  const [isNoticeDismissed, setIsNoticeDismissed] = useState(false);
  const skillInfoModal = useModal();
  const addVenueModal = useModal();
  // Venues picked via the search this session — candidates alongside `venueOptions`.
  const [addedVenues, setAddedVenues] = useState<VenueAddressWithCity[]>([]);
  // "Preferred courts" switch; on with nothing ticked is a plain geo search.
  const [preferredCourtsOn, setPreferredCourtsOn] = useState(filters.venues.length > 0);
  // Set while the sheet is swapped for another modal, so reopening keeps edits.
  const keepStagedOnReopen = useRef(false);

  // Re-seed from the committed filters each time the sheet opens. Deliberately keyed
  // on `isOpen` only: `filters` changing while the sheet is open must not clobber
  // in-progress edits, so the value captured at open time is the one we want.
  useEffect(() => {
    if (!isOpen) return;
    if (keepStagedOnReopen.current) {
      keepStagedOnReopen.current = false;
      return;
    }
    setStaged(filters);
    setPreferredCourtsOn(filters.venues.length > 0);
    setIsNoticeDismissed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const swapTo = (open: () => void) => {
    keepStagedOnReopen.current = true;
    onClose();
    // Next tick, so the new dialog doesn't treat the click that opened it as an
    // outside click and close itself straight away.
    setTimeout(open, 0);
  };

  const closeSkillInfo = () => {
    skillInfoModal.closeModal();
    onReopen?.();
  };

  const closeAddVenue = () => {
    addVenueModal.closeModal();
    onReopen?.();
  };

  const addVenue = (result: AddVenueResult) => {
    const venue = toVenueAddress(result);
    setPreferredCourtsOn(true);
    setAddedVenues((current) =>
      current.some((added) => added.id === venue.id) ? current : [venue, ...current],
    );
    setStaged((prev) =>
      prev.venues.some((selected) => selected.id === venue.id)
        ? prev
        : { ...prev, venues: [venue, ...prev.venues] },
    );
    closeAddVenue();
  };

  const resultCount = useMemo(
    () => (getResultCount ? getResultCount(staged) : null),
    [getResultCount, staged],
  );

  const setField = <K extends keyof FindFilters>(key: K, value: FindFilters[K]) =>
    setStaged((prev) => ({ ...prev, [key]: value }));

  const toggle = (key: ArrayFilterKey, value: string) =>
    setStaged((prev) => {
      const arr = prev[key] as string[];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [key]: next };
    });

  const clear = (key: ArrayFilterKey) => setStaged((prev) => ({ ...prev, [key]: [] }));

  const sortOptions: FilterChipOption<FindSortKey>[] = isGames
    ? GAME_SORT_OPTIONS
    : PLAYER_SORT_OPTIONS;

  const noun = isGames ? 'games' : 'players';

  return (
    <>
      <Modal
        isOpen={isOpen}
        handleClose={onClose}
        classNameRounded="rounded-t-3xl sm:rounded-3xl"
        classNameMaxWidth={isHome ? 'max-w-[40rem]' : 'max-w-md'}
        classNameHeight="max-h-[92dvh]"
      >
        <div className="flex max-h-[92dvh] flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
          <header
            className={classNames(
              'flex shrink-0 items-center justify-between border-b border-color-border-input-lightmode dark:border-color-border-input-darkmode',
              isHome ? 'py-5 max-lg:px-ds-lg lg:px-ds-3xl' : 'px-ds-lg py-ds-lg',
            )}
          >
            <h2
              className={classNames(
                'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                isHome ? 'typography-product-heading-desktop' : 'typography-product-heading-mobile',
              )}
            >
              Filters
            </h2>
            <ButtonText onClick={onClose}>
              <CloseIcon className={isHome ? 'h-6 w-6' : 'h-5 w-5'} />
            </ButtonText>
          </header>

          <div
            className={classNames(
              'flex-1 overflow-y-auto',
              isHome ? 'max-lg:px-ds-lg lg:px-ds-3xl' : 'px-ds-xl',
            )}
          >
            {isHome ? (
              <HomeSections
                staged={staged}
                setField={setField}
                toggle={toggle}
                clear={clear}
                isTodayMode={isTodayMode}
                venueOptions={venueOptions}
                addedVenues={addedVenues}
                preferredCourtsOn={preferredCourtsOn}
                setPreferredCourtsOn={setPreferredCourtsOn}
                onAddVenue={() => swapTo(addVenueModal.openModal)}
                onLearnMoreSkill={() => swapTo(skillInfoModal.openModal)}
                notice={
                  preferencesNotice && !isNoticeDismissed
                    ? {
                        onView: preferencesNotice.onViewPreferences,
                        onDismiss: () => setIsNoticeDismissed(true),
                      }
                    : undefined
                }
              />
            ) : (
              <>
                <FilterSection label="Distance" className="mt-ds-lg">
                  <FilterChipGroup
                    layout="grid"
                    options={DISTANCE_OPTIONS}
                    selected={[staged.distanceBonusMiles]}
                    onToggle={(value) => setField('distanceBonusMiles', value)}
                  />
                </FilterSection>

                {/* Below lg the header drops its location search; neighborhoods narrow instead. */}
                <LocationSection
                  value={staged.neighborhoods}
                  onChange={(rows) => setField('neighborhoods', rows)}
                  className="lg:hidden"
                />

                {isGames ? (
                  <FilterSection label="Date">
                    <div className="flex h-[2.625rem] items-center gap-ds-xs rounded-md bg-color-bg-lightmode-secondary px-ds-lg dark:bg-color-bg-darkmode-secondary">
                      <Calendar className="h-5 w-5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
                      <input
                        type="date"
                        value={staged.date ?? ''}
                        onChange={(event) => setField('date', event.target.value || null)}
                        className="typography-product-body w-full border-0 bg-transparent text-color-text-lightmode-primary outline-none focus:border-0 focus:outline-0 focus:ring-0 dark:text-color-text-darkmode-primary"
                        aria-label="Filter by date"
                      />
                    </div>
                  </FilterSection>
                ) : (
                  <FilterSection label="Days">
                    <FilterChipGroup
                      layout="grid"
                      options={DAY_OPTIONS}
                      selected={staged.daysOfWeek}
                      onToggle={(value) => toggle('daysOfWeek', value)}
                      resetLabel="Any"
                      onReset={() => clear('daysOfWeek')}
                    />
                  </FilterSection>
                )}

                <FilterSection label="Time">
                  <FilterChipGroup
                    layout="grid"
                    options={TIME_OPTIONS}
                    selected={staged.timesOfDay}
                    onToggle={(value) => toggle('timesOfDay', value)}
                    resetLabel="All"
                    onReset={() => clear('timesOfDay')}
                  />
                </FilterSection>

                <FilterSection label="Sort by">
                  <FilterChipGroup
                    layout="grid"
                    options={sortOptions}
                    selected={[staged.sortBy]}
                    onToggle={(value) => setField('sortBy', value)}
                  />
                </FilterSection>

                <FilterSection label="Type">
                  <FilterChipGroup
                    layout="grid"
                    options={TYPE_OPTIONS}
                    selected={staged.formats}
                    onToggle={(value) => toggle('formats', value)}
                  />
                </FilterSection>

                <FilterSection label="Game type">
                  <FilterChipGroup
                    layout="grid"
                    options={GAME_TYPE_OPTIONS}
                    selected={staged.competitiveness}
                    onToggle={(value) => toggle('competitiveness', value)}
                  />
                </FilterSection>

                <FilterSection label="Gender">
                  <FilterChipGroup
                    layout="grid"
                    options={GENDER_OPTIONS}
                    selected={staged.genders}
                    onToggle={(value) => toggle('genders', value)}
                    resetLabel="All"
                    onReset={() => clear('genders')}
                  />
                </FilterSection>

                <FilterSection label="Skill level">
                  <FilterChipGroup
                    layout="grid"
                    options={SKILL_OPTIONS}
                    selected={staged.skillLevels}
                    onToggle={(value) => toggle('skillLevels', value)}
                    resetLabel="Any"
                    onReset={() => clear('skillLevels')}
                  />
                </FilterSection>

                {isGames && (
                  <FilterSection label="Price">
                    <SliderNumberRange
                      rangeMinimum={PRICE_MIN}
                      rangeMaximum={PRICE_MAX}
                      valueMinimum={staged.priceMin}
                      valueMaximum={staged.priceMax}
                      setValueMinumum={(value) => setField('priceMin', value)}
                      setValueMaximum={(value) => setField('priceMax', value)}
                      step={1}
                      decimals={0}
                      prefix="$"
                      isMaximumOpenEnded
                      className="mt-ds-2xl"
                    />
                  </FilterSection>
                )}

                <FilterSection label="Court type">
                  <FilterChipGroup
                    layout="grid"
                    options={COURT_TYPE_OPTIONS}
                    selected={staged.courtTypes}
                    onToggle={(value) => toggle('courtTypes', value)}
                  />
                </FilterSection>
              </>
            )}
          </div>

          <footer
            className={classNames(
              'flex shrink-0 items-center justify-between gap-ds-md border-t border-color-border-input-lightmode dark:border-color-border-input-darkmode',
              isHome ? 'py-ds-xl max-lg:px-ds-lg lg:px-ds-3xl' : 'px-ds-lg py-ds-lg',
            )}
          >
            <ButtonText
              onClick={() => {
                setStaged(makeDefaultFilters(variant));
                setPreferredCourtsOn(false);
              }}
              className={classNames(
                'flex items-center gap-ds-xs rounded-full text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                isHome
                  ? 'typography-product-button-label-large'
                  : 'typography-product-button-label-small',
              )}
            >
              <Erase className="h-5 w-5 shrink-0" />
              Clear all
            </ButtonText>
            <Button
              variant="primary"
              size={isHome ? 'lg' : 'md'}
              isInline
              onClick={() => {
                onApply(staged);
                onClose();
              }}
            >
              {resultCount == null ? 'Apply' : `Show ${resultCount} ${noun}`}
            </Button>
          </footer>
        </div>
      </Modal>
      {isHome && <SkillLevelInfoModal isOpen={skillInfoModal.isOpen} closeModal={closeSkillInfo} />}
      {isHome && (
        <AddVenue
          title="Add location"
          isOpen={addVenueModal.isOpen}
          onClose={closeAddVenue}
          onSubmit={addVenue}
          onSubmitManualAddress={closeAddVenue}
        />
      )}
    </>
  );
}

// --- Home variant ---------------------------------------------------------------

/** Mobile-only: on desktop the location lives in the page header. */
function LocationSection({
  value,
  onChange,
  className,
}: {
  value: Neighborhood[];
  onChange: (rows: Neighborhood[]) => void;
  className: string;
}) {
  const { neighborhoods } = useNeighborhoods();
  return (
    <FilterSection label="Location" className={className}>
      <NeighborhoodSelect
        options={neighborhoods}
        value={value}
        onChange={onChange}
        placeholder={DEFAULT_SEARCH_CENTER_NAME}
      />
    </FilterSection>
  );
}

interface HomeSectionsProps {
  staged: FindFilters;
  setField: <K extends keyof FindFilters>(key: K, value: FindFilters[K]) => void;
  toggle: (key: ArrayFilterKey, value: string) => void;
  clear: (key: ArrayFilterKey) => void;
  isTodayMode: boolean;
  notice?: { onView: () => void; onDismiss: () => void };
  venueOptions?: VenueAddressWithCity[];
  addedVenues: VenueAddressWithCity[];
  preferredCourtsOn: boolean;
  setPreferredCourtsOn: (on: boolean) => void;
  onAddVenue: () => void;
  onLearnMoreSkill: () => void;
}

function HomeSections({
  staged,
  setField,
  toggle,
  clear,
  isTodayMode,
  notice,
  venueOptions = [],
  addedVenues,
  preferredCourtsOn,
  setPreferredCourtsOn,
  onAddVenue,
  onLearnMoreSkill,
}: HomeSectionsProps) {
  const { position } = useGeoLocation();

  const today = dayOfWeekFromDate(new Date());
  const todayOption = AVAILABILITY_DAY_OPTIONS.find((option) => option.value === today);

  const setDays = (days: AvailabilityDay[]) => setField('availabilityDays', days);

  const toggleDay = (day: AvailabilityDay) => {
    if (!staged.availabilityDays.includes(day)) {
      setDays([...staged.availabilityDays, day]);
      return;
    }
    // Drop the day's time buckets with it, so they can't resurface unseen.
    const nextTimesByDay = { ...staged.timesByDay };
    delete nextTimesByDay[day];
    setField('timesByDay', nextTimesByDay);
    setDays(staged.availabilityDays.filter((value) => value !== day));
  };

  const toggleTimeOfDay = (day: AvailabilityDay, time: PlayerFinderTimeOfDayEnum) => {
    const current = staged.timesByDay[day] ?? [];
    const next = current.includes(time) ? current.filter((v) => v !== time) : [...current, time];
    setField('timesByDay', { ...staged.timesByDay, [day]: next });
    // Today mode shows today's row even when its chip is off; turning the chip on
    // keeps the row visible (and clearable) once the pill leaves Today.
    if (isTodayMode && next.length > 0 && !staged.availabilityDays.includes(day)) {
      setDays([...staged.availabilityDays, day]);
    }
  };

  // Rendered in chip order rather than tap order, so rows don't reshuffle.
  const timeRows = isTodayMode
    ? todayOption
      ? [todayOption]
      : []
    : AVAILABILITY_DAY_OPTIONS.filter((option) => staged.availabilityDays.includes(option.value));

  // Candidate cards persist; selection lives in `staged.venues`. Cards come from
  // the caller's options plus anything added via the picker.
  const options = useMemo(() => {
    const seen = new Set<string>();
    return [...addedVenues, ...venueOptions, ...staged.venues].filter((venue) => {
      if (seen.has(venue.id)) return false;
      seen.add(venue.id);
      return true;
    });
  }, [addedVenues, venueOptions, staged.venues]);

  const isVenueSelected = (venueId: string) => staged.venues.some((venue) => venue.id === venueId);

  const toggleVenue = (venue: VenueAddressWithCity) =>
    setField(
      'venues',
      isVenueSelected(venue.id)
        ? staged.venues.filter((selected) => selected.id !== venue.id)
        : [...staged.venues, venue],
    );

  // On: every candidate ticked (deselect to narrow) and travel radius is replaced
  // by the venue list, so its bonus resets rather than lingering unseen. Off: a
  // plain geo search where the radius control shows again.
  const togglePreferredCourts = () => {
    const next = !preferredCourtsOn;
    setPreferredCourtsOn(next);
    setField('venues', next ? options : []);
    if (next) setField('distanceBonusMiles', HOME_DEFAULT_DISTANCE_BONUS_MILES);
  };

  return (
    <>
      {notice && (
        <InfoAlert
          variant="brand"
          desc="Pre-selected based on your preferences."
          onDismiss={notice.onDismiss}
          className="mt-ds-lg"
        >
          <ButtonText
            onClick={notice.onView}
            className="typography-product-caption flex items-center gap-ds-xs !px-0 text-color-text-brand"
          >
            View preferences in profile
            <ArrowRight className="h-4 w-4" />
          </ButtonText>
        </InfoAlert>
      )}

      <LocationSection
        value={staged.neighborhoods}
        onChange={(rows) => setField('neighborhoods', rows)}
        className="mt-ds-lg lg:hidden"
      />

      <FilterSection label="" className="!gap-0 lg:mt-ds-lg lg:border-t-0 lg:pt-0">
        <Switch
          label="Youth programming"
          isActive={staged.youthOnly}
          toggleIsActive={() => setField('youthOnly', !staged.youthOnly)}
          className="!ml-0 !max-w-none !justify-start gap-ds-xl lg:!ml-0"
          labelClassName="typography-product-subheading text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
        />
      </FilterSection>

      <FilterSection
        label="Skill level"
        action={
          <ButtonText
            onClick={onLearnMoreSkill}
            className="typography-product-button-label-medium flex items-center gap-ds-xs !px-0 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
          >
            <InfoCircle className="h-4 w-4" />
            Learn more
          </ButtonText>
        }
      >
        <FilterChipGroup
          layout="grid"
          size="md"
          options={SKILL_OPTIONS}
          selected={staged.skillLevels}
          onToggle={(value) => toggle('skillLevels', value)}
          resetLabel="Any"
          onReset={() => clear('skillLevels')}
        />
      </FilterSection>

      <FilterSection label="When are you usually available?">
        {isTodayMode && (
          <p className="typography-product-text-card text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            Days don&apos;t apply while Today is selected.
          </p>
        )}
        <div
          className={classNames(
            'flex flex-wrap gap-ds-md',
            isTodayMode && 'pointer-events-none opacity-50',
          )}
          aria-disabled={isTodayMode}
        >
          {AVAILABILITY_DAY_OPTIONS.map((option) => (
            <Pill
              key={option.value}
              mode="select"
              selectVariant="primary"
              text={option.label}
              selected={staged.availabilityDays.includes(option.value)}
              onClick={() => toggleDay(option.value)}
            />
          ))}
        </div>

        {timeRows.map((option) => (
          <div key={option.value} className="flex flex-col gap-ds-md">
            <h4 className="typography-product-subheading text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {option.label}:
            </h4>
            <div className="flex flex-wrap gap-ds-md">
              {TIME_OF_DAY_OPTIONS.map((time) => (
                <Pill
                  key={time.value}
                  mode="select"
                  selectVariant="primary"
                  text={time.label}
                  selected={(staged.timesByDay[option.value] ?? []).includes(time.value)}
                  onClick={() => toggleTimeOfDay(option.value, time.value)}
                />
              ))}
            </div>
          </div>
        ))}
      </FilterSection>

      <FilterSection
        label="Preferred courts"
        actionInline
        action={
          <Switch
            isActive={preferredCourtsOn}
            toggleIsActive={togglePreferredCourts}
            className="!ml-0 !max-w-none lg:!ml-0"
          />
        }
        className={classNames(!preferredCourtsOn && '!gap-0')}
      >
        {preferredCourtsOn &&
          (options.length > 0 ? (
            <>
              <CardsSection
                data={options}
                gap="1rem"
                visibleCards={{ base: 2, sm: 3 }}
                // Selected cards draw a 3px ring + drop shadow; bleed the scroller out
                // so overflow doesn't clip them, and keep snap targets inside the inset.
                cardsWrapperClassName="-mx-1 -my-6 px-1 py-6 scroll-px-1"
                renderCard={(venue) => (
                  <LocationCard
                    key={venue.id}
                    venue={{
                      name: venue.title ?? '',
                      imageUrl: venue.images?.[0],
                      isIndoor: (venue.indoorCourtCount ?? 0) > 0,
                      distanceMiles: venueDistanceMiles(position, venue),
                      primarySport: venue.primarySport,
                    }}
                    selected={isVenueSelected(venue.id)}
                    onToggle={() => toggleVenue(venue)}
                    className="!w-full"
                  />
                )}
              />
              <ButtonText
                size="sm"
                onClick={onAddVenue}
                className="relative z-[1] flex items-center gap-ds-xs self-start !px-0 text-color-text-brand"
              >
                <PlusPlain className="h-4 w-4 shrink-0" /> Add a venue
              </ButtonText>
            </>
          ) : (
            <Button
              variant="brand"
              size="md"
              isInline
              onClick={onAddVenue}
              iconLeft={<PlusPlain className="h-4 w-4" />}
              className="self-start"
            >
              Add location
            </Button>
          ))}
      </FilterSection>

      {!preferredCourtsOn && (
        <FilterSection label="Travel radius">
          <FilterChipGroup
            layout="grid"
            size="md"
            options={HOME_DISTANCE_OPTIONS}
            selected={[staged.distanceBonusMiles]}
            onToggle={(value) => setField('distanceBonusMiles', value)}
          />
        </FilterSection>
      )}

      <FilterSection label="Price">
        <SliderNumberRange
          rangeMinimum={PRICE_MIN}
          rangeMaximum={PRICE_MAX}
          valueMinimum={staged.priceMin}
          valueMaximum={staged.priceMax}
          setValueMinumum={(value) => setField('priceMin', value)}
          setValueMaximum={(value) => setField('priceMax', value)}
          step={1}
          decimals={0}
          prefix="$"
          isMaximumOpenEnded
          className="mt-ds-2xl"
        />
      </FilterSection>
    </>
  );
}
